import type { ReactElement, RefObject } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { MapConfigResponse } from '@shared/api';
import { TILE_SIZE, milesToPixels } from '../../lib/mapFit';
import { buildTileGrid, fitBoundsView, loadGeocode, loadMapConfig, project } from './GeoMapCanvas';
import type { FitPad } from './GeoMapCanvas';
import {
  CONTIGUOUS_US_BOUNDS,
  NON_CONTIGUOUS,
  boundsOfRings,
  resolveStateName,
  stateRings,
  useUsStates,
} from './usStates';
import type { FlatRing, UsStatesData } from './usStates';

/**
 * A static, non-interactive map for the multi-market geo_strategy layouts.
 * Shares GeoMapCanvas's map-config + geocode caches, tile grid and
 * projection, and its fit-to-bounds helper. One component, three views:
 *
 *  - `radius`: centered on a market, zoomed so the REAL sizing radius is a
 *    large circle (a fraction of the shorter side), brand-tinted + outlined.
 *  - `us`: fitted to the contiguous US (national map, or the 7+ list map).
 *  - `states`: fitted to the union of the named states.
 *
 * `tint` paints the contiguous US ('us') or the named states: brand tint +
 * crisp outline, with a light veil over everything outside so the target
 * reads as the target. Pins can carry a text chip (name + count), placed so
 * chips do not overlap. No ZIP polygons, no editing.
 */

export interface MarketPin {
  label: string;
  /** Known coordinates; geocoded from `label` when absent. */
  center?: { lat: number; lng: number };
  /** Rank badge text on the pin (many-market maps). */
  badge?: string;
  /** Text chip beside the pin (e.g. "Minneapolis, MN  2,982"). */
  tag?: string;
}

interface ResolvedPin extends MarketPin {
  lat: number;
  lng: number;
}

export type MapView =
  | { kind: 'radius'; miles: number }
  | { kind: 'us' }
  | { kind: 'states'; names: string[] };

const MAPBOX_MAX_DIMENSION = 1280;
/** The radius circle's diameter as a fraction of the box's shorter side. */
const RADIUS_FILL = 0.58;
const NO_PAD: FitPad = { top: 0, right: 0, bottom: 0, left: 0 };

function useBox(): [RefObject<HTMLDivElement>, { width: number; height: number }] {
  const ref = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState({ width: 600, height: 600 });
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const observer = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (rect && rect.width > 4 && rect.height > 4) setBox({ width: rect.width, height: rect.height });
    });
    observer.observe(el);
    if (el.clientWidth > 4 && el.clientHeight > 4) setBox({ width: el.clientWidth, height: el.clientHeight });
    return () => observer.disconnect();
  }, []);
  return [ref, box];
}

function usePins(pins: MarketPin[]): ResolvedPin[] {
  const key = pins.map((p) => `${p.label}|${p.center?.lat ?? ''},${p.center?.lng ?? ''}|${p.badge ?? ''}|${p.tag ?? ''}`).join(';');
  const [resolved, setResolved] = useState<ResolvedPin[]>([]);
  useEffect(() => {
    let live = true;
    void Promise.all(
      pins.map(async (pin): Promise<ResolvedPin | null> => {
        if (pin.center) return { ...pin, lat: pin.center.lat, lng: pin.center.lng };
        const res = await loadGeocode(pin.label);
        if ('error' in res) return null;
        return { ...pin, lat: res.lat, lng: res.lng };
      }),
    ).then((all) => {
      if (live) setResolved(all.filter((p): p is ResolvedPin => p !== null));
    });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return resolved;
}

type Pt = { x: number; y: number };

function ringPath(ring: FlatRing, toPx: (lat: number, lng: number) => Pt): string {
  let d = '';
  for (let i = 0; i + 1 < ring.length; i += 2) {
    const p = toPx(ring[i + 1]!, ring[i]!);
    d += `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)} ${p.y.toFixed(1)}`;
  }
  return d;
}

/** Chip footprint, design px: 20px bold text. */
const chipSize = (tag: string): { w: number; h: number } => ({ w: Math.round(tag.length * 10.4 + 26), h: 38 });

interface Placed {
  pin: ResolvedPin;
  x: number;
  y: number;
  chip: { left: number; top: number; w: number; h: number } | null;
}

/** Greedy chip placement: right, left, above, below of each pin; first free spot wins. */
function placeChips(pins: Array<{ pin: ResolvedPin; x: number; y: number }>, box: { width: number; height: number }): Placed[] {
  const taken: Array<{ left: number; top: number; w: number; h: number }> = pins.map(({ x, y }) => ({
    left: x - 12,
    top: y - 12,
    w: 24,
    h: 24,
  }));
  const hit = (a: { left: number; top: number; w: number; h: number }, b: typeof a) =>
    a.left < b.left + b.w && a.left + a.w > b.left && a.top < b.top + b.h && a.top + a.h > b.top;
  return pins.map(({ pin, x, y }, index) => {
    if (!pin.tag) return { pin, x, y, chip: null };
    const { w, h } = chipSize(pin.tag);
    const gap = 14;
    const candidates = [
      { left: x + gap, top: y - h / 2 },
      { left: x - gap - w, top: y - h / 2 },
      { left: x - w / 2, top: y - gap - h },
      { left: x - w / 2, top: y + gap },
    ].map((c) => ({ ...c, w, h }));
    const inBounds = (c: (typeof candidates)[number]) =>
      c.left >= 4 && c.top >= 4 && c.left + c.w <= box.width - 4 && c.top + c.h <= box.height - 4;
    const others = taken.filter((_, i) => i !== index);
    const free = candidates.find((c) => inBounds(c) && !others.some((t) => hit(c, t)));
    const chosen = free ?? candidates.find(inBounds) ?? candidates[0]!;
    taken[index] = chosen;
    return { pin, x, y, chip: chosen };
  });
}

export function MarketMap({
  pins,
  view,
  tint,
  pad = NO_PAD,
  className,
}: {
  pins: MarketPin[];
  view: MapView;
  /** Paint the contiguous US ('us') or the named states: brand tint, crisp outline, veil outside. */
  tint?: 'us' | string[];
  /** Keep this much of the box clear of the fitted target (floating title). */
  pad?: FitPad;
  className?: string;
}): ReactElement {
  const [boxRef, box] = useBox();
  const [mapConfig, setMapConfig] = useState<MapConfigResponse | null>(null);
  const [imgFailed, setImgFailed] = useState(false);
  const resolved = usePins(pins);
  const needsStates = view.kind === 'states' || Boolean(tint);
  const states = useUsStates(needsStates);

  useEffect(() => {
    let live = true;
    void loadMapConfig().then((cfg) => {
      if (live) setMapConfig(cfg);
    });
    return () => {
      live = false;
    };
  }, []);

  const provider = mapConfig?.provider ?? 'none';
  const useMapbox = provider === 'mapbox' && Boolean(mapConfig?.mapboxToken) && !imgFailed;
  const useOsm = provider === 'osm' && !imgFailed;

  const tintedNames = useMemo(() => {
    if (!tint || !states) return [] as string[];
    if (tint === 'us') return Object.keys(states.states).filter((n) => !NON_CONTIGUOUS.has(n));
    return tint.map((l) => resolveStateName(l, states)).filter((n): n is string => n !== null);
  }, [tint, states]);

  // The camera: center + fractional zoom for the current view.
  let center: { lat: number; lng: number } | null = null;
  let zoom = 4;
  if (view.kind === 'us') {
    const fit = fitBoundsView(CONTIGUOUS_US_BOUNDS.sw, CONTIGUOUS_US_BOUNDS.ne, box.width, box.height, pad);
    center = fit.center;
    zoom = fit.zoom;
  } else if (view.kind === 'states') {
    const names = states ? view.names.map((l) => resolveStateName(l, states)).filter((n): n is string => n !== null) : [];
    const bounds = states && names.length > 0 ? boundsOfRings(stateRings(states, names)) : null;
    if (bounds) {
      const fit = fitBoundsView(bounds.sw, bounds.ne, box.width, box.height, pad);
      center = fit.center;
      zoom = fit.zoom;
    }
  } else if (resolved[0]) {
    center = { lat: resolved[0].lat, lng: resolved[0].lng };
    const px0 = milesToPixels(view.miles, 0, center.lat);
    zoom = Math.min(16, Math.max(3, Math.log2((RADIUS_FILL * Math.min(box.width, box.height)) / (2 * px0))));
  }

  let background: ReactElement | null = null;
  if (center && useMapbox && mapConfig?.mapboxToken) {
    const scale = Math.min(1, MAPBOX_MAX_DIMENSION / box.width, MAPBOX_MAX_DIMENSION / box.height);
    const w = Math.max(1, Math.round(box.width * scale));
    const h = Math.max(1, Math.round(box.height * scale));
    // Mapbox world is 512px at z0 (ours 256) and the image may be smaller than the box.
    const mapboxZoom = Math.max(0, zoom - 1 + Math.log2(scale));
    const url =
      `https://api.mapbox.com/styles/v1/mapbox/light-v11/static/${center.lng},${center.lat},${mapboxZoom.toFixed(2)}/${w}x${h}@2x` +
      `?access_token=${encodeURIComponent(mapConfig.mapboxToken)}`;
    background = (
      <img className="mtm-geo-bg-image" src={url} width={box.width} height={box.height} alt="" onError={() => setImgFailed(true)} />
    );
  } else if (center && useOsm) {
    // OSM tiles exist at integer zooms only: lay out the nearest integer zoom
    // and scale the tile layer about the map center to the exact fractional fit.
    const tileZoom = Math.min(18, Math.max(0, Math.round(zoom)));
    const s = 2 ** (zoom - tileZoom);
    const grid = buildTileGrid(center, tileZoom, box.width / s, box.height / s);
    const cx = box.width / s / 2 - grid.offsetX;
    const cy = box.height / s / 2 - grid.offsetY;
    background = (
      <div
        className="mtm-geo-bg-tiles"
        style={{
          left: box.width / 2 - cx,
          top: box.height / 2 - cy,
          width: grid.gridWidth,
          height: grid.gridHeight,
          transformOrigin: `${cx}px ${cy}px`,
          transform: `scale(${s})`,
        }}
      >
        {grid.tiles.map((tile) => (
          <img
            key={`${tile.zoom}/${tile.tileX}/${tile.tileY}`}
            className="mtm-geo-bg-tile"
            src={`https://tile.openstreetmap.org/${tile.zoom}/${tile.tileX}/${tile.tileY}.png`}
            width={TILE_SIZE}
            height={TILE_SIZE}
            style={{ left: tile.left, top: tile.top }}
            alt=""
            onError={() => setImgFailed(true)}
          />
        ))}
      </div>
    );
  }

  const toPx = (lat: number, lng: number): Pt => project(lat, lng, center!, zoom, box.width, box.height);
  const shownPins = view.kind === 'radius' ? resolved.slice(0, 1) : resolved;
  const placed = center
    ? placeChips(
        shownPins.map((pin) => ({ pin, ...toPx(pin.lat, pin.lng) })),
        box,
      )
    : [];

  return (
    <div ref={boxRef} className={`mv3-market-map${className ? ` ${className}` : ''}`} data-map-theme="light">
      {background}
      {center ? (
        <svg className="mv3-market-overlay" width={box.width} height={box.height} viewBox={`0 0 ${box.width} ${box.height}`} aria-hidden="true">
          {states && tintedNames.length > 0 ? <StateLayer data={states} names={tintedNames} us={tint === 'us'} box={box} toPx={toPx} /> : null}
          {view.kind === 'radius' ? (
            <circle
              className="mv3-market-radius"
              cx={placed[0]?.x ?? box.width / 2}
              cy={placed[0]?.y ?? box.height / 2}
              r={milesToPixels(view.miles, zoom, center.lat)}
            />
          ) : null}
        </svg>
      ) : null}
      {center ? (
        <div className="mv3-market-pins" aria-hidden="true">
          {placed.map(({ pin, x, y, chip }) => (
            <span key={pin.label}>
              <span
                className={`mv3-market-pin${pin.badge ? ' mv3-market-pin-badge' : ''}${view.kind === 'radius' ? ' mv3-market-pin-center' : ''}`}
                style={{ left: x, top: y }}
              >
                {pin.badge ?? null}
              </span>
              {chip && pin.tag ? (
                <span className="mv3-market-chip" style={{ left: chip.left, top: chip.top, height: chip.h }}>
                  {pin.tag}
                </span>
              ) : null}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** Veil outside the target, brand tint + state borders inside, a crisp outer outline. */
function StateLayer({
  data,
  names,
  us,
  box,
  toPx,
}: {
  data: UsStatesData;
  names: string[];
  us: boolean;
  box: { width: number; height: number };
  toPx: (lat: number, lng: number) => Pt;
}): ReactElement {
  const rings = stateRings(data, names);
  const fill = rings.map((r) => `${ringPath(r, toPx)}Z`).join('');
  const outline = us ? data.outline.map((r) => ringPath(r, toPx)).join('') : fill;
  return (
    <>
      <path className="mv3-market-veil" fillRule="evenodd" d={`M0 0H${box.width}V${box.height}H0Z${fill}`} />
      <path className="mv3-market-tint" fillRule="evenodd" d={fill} />
      {us ? <path className="mv3-market-state-borders" d={fill} /> : null}
      <path className="mv3-market-outline" d={outline} />
    </>
  );
}
